import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './firebase';

export interface DeviceInfoLog {
    platform: string;
    model: string;
    operatingSystem: string;
    osVersion: string;
    manufacturer: string;
    isVirtual: boolean;
    memUsed?: number;
    userAgent: string;
    screenResolution: string;
    timestamp: any;
    uid?: string;
    type: 'installation' | 'auth_session';
}

/**
 * Capture hardware and environment details securely.
 */
export const getDeviceDetails = async () => {
    const info = await Device.getInfo();
    const battery = await Device.getBatteryInfo();

    return {
        platform: Capacitor.getPlatform(),
        model: info.model,
        operatingSystem: info.operatingSystem,
        osVersion: info.osVersion,
        manufacturer: info.manufacturer,
        isVirtual: info.isVirtual,
        ...(typeof info.memUsed === 'number' ? { memUsed: info.memUsed } : {}),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        batteryLevel: battery.batteryLevel,
        isCharging: battery.isCharging
    };
};

/**
 * Log device installation information for the first time.
 */
export const logDeviceInstallation = async (uid?: string) => {
    try {
        const details = await getDeviceDetails();
        const installationId = localStorage.getItem('clue200_install_id') ||
            `install_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

        if (!localStorage.getItem('clue200_install_id')) {
            localStorage.setItem('clue200_install_id', installationId);
        }

        const installRef = doc(db, 'device_installations', installationId);
        await setDoc(installRef, {
            ...details,
            uid: uid || null,
            firstInstallAt: serverTimestamp(),
            lastSeenAt: serverTimestamp(),
            type: 'installation'
        }, { merge: true });

        console.log('✅ Device installation logged securely.');
        return installationId;
    } catch (error) {
        console.warn('Could not log device installation:', error);
    }
};

/**
 * Log a secure authentication session timestamp with device fingerprints.
 */
export const logAuthSession = async (uid: string, method: string) => {
    try {
        const details = await getDeviceDetails();
        await addDoc(collection(db, 'auth_logs'), {
            uid,
            method,
            ...details,
            timestamp: serverTimestamp(),
            type: 'auth_session'
        });
        console.log('✅ Auth session logged for user:', uid);
    } catch (error) {
        console.warn('Could not log auth session:', error);
    }
};
